import { Box } from '@chakra-ui/react';
import type { ReactNode } from 'react';

type BadgeVariant = 'neutral' | 'success' | 'error' | 'warning' | 'brand';
type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: ReactNode;
}

const variantTokens: Record<BadgeVariant, { bg: string; color: string; borderColor: string }> = {
  neutral: { bg: 'status.neutral.bg', color: 'status.neutral.text', borderColor: 'status.neutral.border' },
  success: { bg: 'status.success.bg', color: 'status.success.text', borderColor: 'status.success.border' },
  error: { bg: 'status.error.bg', color: 'status.error.text', borderColor: 'status.error.border' },
  warning: { bg: 'status.warning.bg', color: 'status.warning.text', borderColor: 'status.warning.border' },
  brand: { bg: 'brand.50', color: 'text.brand', borderColor: 'border.brand' },
};

export function Badge({ variant = 'neutral', size = 'sm', children }: BadgeProps) {
  const v = variantTokens[variant];
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="1"
      px={size === 'sm' ? '8px' : '10px'}
      py={size === 'sm' ? '2px' : '4px'}
      borderRadius="full"
      border="1px solid"
      fontWeight="500"
      fontSize={size === 'sm' ? '12px' : '14px'}
      lineHeight={size === 'sm' ? '18px' : '20px'}
      whiteSpace="nowrap"
      bg={v.bg}
      color={v.color}
      borderColor={v.borderColor}
    >
      {children}
    </Box>
  );
}

export function SentimentBadge({ sentiment }: { sentiment: 'calm' | 'caution' | 'warning' }) {
  const map: Record<string, BadgeVariant> = {
    calm: 'success',
    caution: 'warning',
    warning: 'error',
  };
  return <Badge variant={map[sentiment]}>{sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}</Badge>;
}

export function RiskBadge({ level }: { level: 'low' | 'moderate' | 'elevated' | 'high' }) {
  const map: Record<string, BadgeVariant> = {
    low: 'success',
    moderate: 'neutral',
    elevated: 'warning',
    high: 'error',
  };
  const labels: Record<string, string> = {
    low: 'Low risk',
    moderate: 'Moderate risk',
    elevated: 'Elevated risk',
    high: 'High risk',
  };
  return <Badge variant={map[level]}>{labels[level]}</Badge>;
}

export function RegimeBadge({ regime }: { regime: string }) {
  const map: Record<string, BadgeVariant> = {
    dead: 'neutral',
    ranging: 'neutral',
    trending_up: 'success',
    trending_down: 'warning',
    volatile: 'error',
  };
  const labels: Record<string, string> = {
    dead: 'Dead',
    ranging: 'Ranging',
    trending_up: 'Trending Up',
    trending_down: 'Trending Down',
    volatile: 'Volatile',
  };
  return <Badge variant={map[regime] ?? 'neutral'}>{labels[regime] ?? regime}</Badge>;
}

export function RecommendationBadge({ recommendation }: { recommendation: string }) {
  const map: Record<string, BadgeVariant> = {
    hold: 'neutral',
    buy: 'brand',
    reduce: 'warning',
    exit: 'error',
  };
  return (
    <Badge variant={map[recommendation] ?? 'neutral'} size="md">
      {recommendation.toUpperCase()}
    </Badge>
  );
}
